import React, { useState, useEffect, useLayoutEffect } from 'react';
import { showConfirm, showSuccess, showError } from "../utils/SweetAlert";
import { useNavigate } from "react-router-dom";
import propTypes from 'prop-types';

import Button from 'elements/Button';
import { InputDate, InputNumber } from 'elements/Form';
import { format, parseISO, isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

function BookingForm({ itemDetails, bookingItems, startBooking }) {
  const navigate = useNavigate();
  const timeZone = 'Asia/Jakarta';

  const [excludedDates, setExcludedDates] = useState([]);
  const [data, setData] = useState(null); // Null sementara saat bookingItems belum ready

  // Hitung tanggal yang sudah dibooking
  const getExcludedDates = (bookings) => {
    const dates = [];

    bookings.forEach((booking) => {
      let current = new Date(booking.start);
      const end = new Date(booking.end);

      while (current < end) {
        dates.push(new Date(current).toISOString());
        current.setDate(current.getDate() + 1);
      }
    });

    return dates.map((d) => parseISO(d));
  };

  // Saat bookingItems sudah terisi → inisialisasi tanggal awal & excludedDates
  useEffect(() => {
    if (!bookingItems || bookingItems.length === 0) return;

    const dates = getExcludedDates(bookingItems);
    setExcludedDates(dates);

    const today = new Date();
    const isTodayBooked = dates.some(date => isSameDay(date, today));
    const initialStart = isTodayBooked
      ? new Date(today.setDate(today.getDate() + 1))
      : today;

    const initialEnd = itemDetails.category_id === "CAT-GEDUNG"
      ? new Date(initialStart)
      : new Date(initialStart.getTime() + 24 * 60 * 60 * 1000);

    setData({
      duration: 1,
      date: {
        startDate: initialStart,
        endDate: initialEnd,
        minDate: today,
        key: "selection"
      }
    });
  }, [bookingItems, itemDetails.category_id]);

  // Hitung durasi saat tanggal berubah
  useLayoutEffect(() => {
    if (!data) return;

    const { startDate, endDate } = data.date;
    let duration = 1;

    if (!isSameDay(startDate, endDate)) {
      const ms = Math.abs(endDate - startDate);
      duration = itemDetails.category_id === "CAT-GEDUNG"
        ? ms / (1000 * 60 * 60 * 24) + 1
        : ms / (1000 * 60 * 60 * 24);
    }

    setData(prev => ({ ...prev, duration }));
  }, [data?.date, itemDetails.category_id]);

  // Update saat user input manual (duration atau tanggal)
  const updateData = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  // Tombol Booking ditekan
  const handleBooking = () => {
    if (!data || data.duration === 0) {
      showError("Tanggal belum dipilih", "Silakan pilih tanggal check-in dan check-out");
      return;
    }

    if (
        itemDetails.category_id !== "CAT-GEDUNG" &&
        isSameDay(data.date.startDate, data.date.endDate)
    ) {
        showError("Tanggal tidak valid", "Tanggal check-in dan check-out tidak boleh sama.");
        return;
    }

    const start = utcToZonedTime(data.date.startDate, timeZone);
    const end = utcToZonedTime(data.date.endDate, timeZone);

    start.setHours(14, 0, 0, 0);
    end.setHours(12, 0, 0, 0);

    const startFormatted = format(start, 'yyyy-MM-dd HH:mm:ss');
    const endFormatted = format(end, 'yyyy-MM-dd HH:mm:ss');

    startBooking({
      _id: itemDetails.item_id,
      duration: data.duration,
      date: {
        startDate: startFormatted,
        endDate: endFormatted,
      }
    });

    navigate(`/checkout/${itemDetails.item_id}`);
  };

  if (!data) return null; // loading state bisa ditambahkan di sini

  return (
    <div
  className="card bordered px-4 py-5 px-md-5 py-md-5"
  style={{
    padding: "40px 20px", // fallback untuk browser tanpa Bootstrap
  }}
>
      <h4 className="mb-3">Start Booking</h4>
      <h5 className="h3 text-teal mb-4">
        Rp {itemDetails.price.toLocaleString("id-ID")}{" "}
        <span className="text-gray-500 font-weight-light">/{itemDetails.unit}</span>
      </h5>

      <label htmlFor="duration">How long you will stay?</label>
      <InputNumber
        max={30}
        suffix={itemDetails.category_id === "CAT-GEDUNG"? " day":" night"}
        isSuffixPlural
        onChange={updateData}
        name="duration"
        value={data.duration}
      />

      <label htmlFor="date">Pick a date</label>
      <InputDate
        onChange={updateData}
        name="date"
        value={data.date}
        excludeDates={excludedDates}
        allowSameDay={itemDetails.category_id === "CAT-GEDUNG"}
      />

      <h6 className="text-gray-500 font-weight-light mb-4">
        You will pay{" "}
        <span className="text-gray-900">
          Rp {(itemDetails.price * data.duration).toLocaleString("id-ID")}
        </span>{" "}
        for{" "}
        <span className="text-gray-900">
          {data.duration} {itemDetails.unit}
        </span>
      </h6>

      <Button
        className="btn"
        hasShadow
        isPrimary
        isBlock
        onClick={handleBooking}
      >
        Continue to Book
      </Button>
    </div>
  );
}

BookingForm.propTypes = {
  itemDetails: propTypes.object.isRequired,
  bookingItems: propTypes.array.isRequired,
  startBooking: propTypes.func.isRequired,
};

export default BookingForm;
